import { Formik, FormikHelpers, FormikProps } from "formik";
import moment from "moment";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BlankMediaPeriodInfo,
  BlankMediaTypeProfessionalUserApplication,
  EquipmentItemViewModel,
  ItemViewModel,
  SubmitBlankMediaImporterApplication,
  SubmitBlankMediaProfessionalUserApplication,
} from "../../../../../generated/data-contracts";
import {
  AvisUtils,
  BlankMediaUrls,
  BlankMediaUtils,
  ErrorUtils,
  IAxiosError,
  api,
  prepareCollectionForPost,
  nullsToUndefined,
} from "../../../../../utils";
import { AvisForm, AvisLoader } from "../../../../shared/components";
import { SaveAndSubmit } from "../../../../shared/components/form";
import { AvisConfirmModalShort } from "../../../../shared/components/modal";
import { AvisContext } from "../../../../shared/context";
import { ScrollToFieldError } from "../../../../shared/formik/ScrollToFieldError";
import { BlankMediaTypeProfessionalUserApplicationForm } from "./BlankMediaTypeProfessionalUserApplicationForm";
import { validationSchemaBlankMediaProfessionalUserApplication } from "./validations";
import _ from "lodash";
import { AvisConstants } from "../../../../../data/avisConstants";

export function BlankMediaTypeProfessionalUserApplicationPage() {
  const { res } = useContext(AvisContext);
  const [error, setError] = useState<IAxiosError>();
  const [applicationData, setApplicationData] =
    useState<BlankMediaTypeProfessionalUserApplication>();
  const [isLoadingApplicationData, setIsLoadingApplicationData] =
    useState<boolean>(true);
  const [equipments, setEquipments] = useState<EquipmentItemViewModel[]>();
  const [isLoadingEquipments, setIsLoadingEquipments] = useState<boolean>(true);

  const [periodInfo, setPeriodInfo] = useState<BlankMediaPeriodInfo>();
  const [isLoadingPeriodInfo, setIsLoadingPeriodInfo] = useState<boolean>(true);

  const [firstLoading, setFirstLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [noDealAccepted, setNoDealAccepted] = useState<boolean>(false);

  const [importersAndManufacturers, setImportersAndManufacturers] =
    useState<ItemViewModel[]>();
  const [
    isLoadingImportersAndManufacturers,
    setIsLoadingImportersAndManufacturers,
  ] = useState<boolean>(true);

  const location = useLocation();
  const navigate = useNavigate();

  const urlParams = useMemo(
    () => {
      return BlankMediaUtils.getUrlParams();
    },
    //Needed to reload urlParams on location change without actually using location
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location],
  );

  const pageTitleValue = useMemo(() => {
    //If application id is provided - wait till appData loads
    if (!applicationData && !!urlParams.applicationId) return;

    if (!urlParams.applicationId)
      return res("blankMedia_LandingPage_button_NewSubmission");

    return applicationData?.documentNumber;
  }, [applicationData, res, urlParams.applicationId]);

  const hasNoPortalReport = useMemo(() => {
    if (!applicationData?.blankMediaTypeId) return false;

    return BlankMediaUtils.hasNoPortalReport(
      applicationData?.portalReportStatusType,
      applicationData?.dealNotOccuredPortal,
      applicationData?.equipApplications?.length === 0,
    );
  }, [applicationData]);

  const loadRelatedPeriodData = useCallback(
    (dateFrom?: Date, dateTo?: Date) => {
      if (!dateFrom || !dateTo) {
        setImportersAndManufacturers(undefined);
        setPeriodInfo(undefined);
        return;
      }
      setIsLoadingImportersAndManufacturers(true);
      setIsLoadingPeriodInfo(true);

      const dateFromValue = moment(dateFrom).format(
        AvisConstants.date_format_ISO,
      );
      const dateToValue = moment(dateTo).format(AvisConstants.date_format_ISO);

      api
        .blankMediaUsersGetActivePersonByType({
          DateFrom: dateFromValue,
          DateTo: dateToValue,
          BlankMediaUseTypes: ["Importer", "Manufacturer"],
        } as any)
        .then((res) => {
          setImportersAndManufacturers(res.data);
        })
        .finally(() => {
          setIsLoadingImportersAndManufacturers(false);
        });
      api
        .blankMediaUsersGetBlankMediaPeriodInfo({
          DateFrom: dateFromValue,
          DateTo: dateToValue,
          BlankMediaUseTypes: ["ProfessionalUser"],
          BlankMediaTypeId: urlParams.applicationId,
        } as any)
        .then((res) => {
          setPeriodInfo(res.data);
        })
        .finally(() => {
          setIsLoadingPeriodInfo(false);
        });
    },
    [urlParams],
  );

  useEffect(() => {
    setIsLoadingEquipments(true);

    api
      .classifiersGetActiveEquipments()
      .then((res) => {
        setEquipments(res.data);
      })
      .catch(setError)
      .finally(() => {
        setIsLoadingEquipments(false);
      });
  }, []);

  const getInitValues = useCallback(
    (data: BlankMediaTypeProfessionalUserApplication | undefined) => {
      return data?.blankMediaTypeId === undefined
        ? {
            blankMediaTypeId: undefined,
            dealNotOccuredPortal: false,
            equipApplications: [],
            dateFrom: "",
            dateTo: "",
            submitReport: false,
          }
        : {
            blankMediaTypeId: data?.blankMediaTypeId,
            dealNotOccuredPortal: data?.dealNotOccuredPortal || false,
            dateFrom: data.dateFrom ?? "",
            dateTo: data.dateTo ?? "",
            equipApplications: data?.equipApplications,
            submitReport: false,
          };
    },
    [],
  );

  const loadApplicationData = useCallback(
    (
      formikHelper?: FormikHelpers<SubmitBlankMediaProfessionalUserApplication>,
    ) => {
      if (!urlParams?.applicationType) return;
      if (!urlParams?.applicationId) {
        setApplicationData(undefined);
        setIsLoadingApplicationData(false);
        setFirstLoading(false);
      }

      if (!urlParams.applicationId) return;

      setIsLoadingApplicationData(true);
      api
        .blankMediaUsersGetProfessionalUserApplicationDataById(
          urlParams.applicationId,
        )
        .then((res) => {
          const dataNormalized =
            nullsToUndefined<BlankMediaTypeProfessionalUserApplication>(
              res.data,
            );
          setApplicationData(dataNormalized);
          setNoDealAccepted(dataNormalized.dealNotOccuredPortal || false);
          loadRelatedPeriodData(
            moment(dataNormalized.dateFrom).toDate(),
            moment(dataNormalized.dateTo).toDate(),
          );
          if (formikHelper)
            formikHelper.resetForm({ values: getInitValues(dataNormalized) });
        })
        .catch(setError)
        .finally(() => {
          setIsLoadingApplicationData(false);
          setFirstLoading(false);
        });
    },
    [urlParams, loadRelatedPeriodData, getInitValues],
  );

  useEffect(() => {
    loadApplicationData();
  }, [loadApplicationData]);

  const isLoadingFirstTime = useMemo(() => {
    return (
      (isLoadingApplicationData ||
        isLoadingEquipments ||
        isLoadingImportersAndManufacturers ||
        isLoadingPeriodInfo) &&
      firstLoading
    );
  }, [
    isLoadingApplicationData,
    isLoadingEquipments,
    isLoadingImportersAndManufacturers,
    isLoadingPeriodInfo,
    firstLoading,
  ]);

  const isLoadedClassifiers = useMemo(() => !!equipments, [equipments]);

  const isSectionsVisible = useCallback(
    (formikProps: FormikProps<SubmitBlankMediaProfessionalUserApplication>) => {
      const sectionsVisible1HasDate = !!formikProps?.values.dateFrom;
      const sectionsVisible2PeriodInfo =
        periodInfo?.personHasActiveRole && !periodInfo?.periodHasApplication;

      const sectionsVisible =
        sectionsVisible1HasDate && sectionsVisible2PeriodInfo;

      return sectionsVisible ?? false;
    },
    [periodInfo],
  );

  const renderActionButtons = useCallback(
    (
      editAllowed: boolean,
      formikProps: FormikProps<SubmitBlankMediaProfessionalUserApplication>,
    ) => {
      if (!editAllowed || isLoadingFirstTime) return;

      const noDealApproveNeeded =
        formikProps.values.dealNotOccuredPortal && !noDealAccepted;

      return (
        <SaveAndSubmit
          formikProps={formikProps}
          disabled={
            isSubmitting ||
            !isLoadedClassifiers ||
            !isSectionsVisible(formikProps) ||
            noDealApproveNeeded
          }
          setIsModalOpen={setIsModalOpen}
        />
      );
    },
    [
      isLoadingFirstTime,
      noDealAccepted,
      isSubmitting,
      isLoadedClassifiers,
      isSectionsVisible,
    ],
  );

  const handleSubmit = useCallback(
    (
      formikValues: SubmitBlankMediaProfessionalUserApplication,
      formikHelpers: FormikHelpers<SubmitBlankMediaProfessionalUserApplication>,
    ) => {
      setIsSubmitting(true);

      const values = _.cloneDeep(formikValues);
      if (values.dealNotOccuredPortal) {
        //Empty bag rows with possible incomplete data
        values.equipApplications = [];
      } else {
        prepareCollectionForPost(values, "equipApplications");
      }

      api
        .blankMediaUsersPostProfessionalApplicationData(values)
        .then((response) => {
          if (values.submitReport) {
            setSubmitted(true);
          }
          loadApplicationData(formikHelpers);

          ErrorUtils.showSuccessfulMessage(
            res("message_Success"),
            values.submitReport
              ? res("blankMedia_Application_notification_Submit")
              : res("blankMedia_Application_notification_Save"),
            500,
          );
          if (!urlParams.applicationId) {
            //If new application page - navigate to view with id & set bag id value
            formikHelpers.setFieldValue("blankMediaTypeId", response.data);
            navigate(
              BlankMediaUtils.getUrlWithParams(
                BlankMediaUrls.applicationViewWithId,
                {
                  applicationId: response.data,
                  applicationType: "ProfessionalUser",
                },
              ),
            );
          }
        })
        .catch((err) => ErrorUtils.handleFormValidation(err, formikHelpers))
        .finally(() => {
          setIsSubmitting(false);
          setIsModalOpen(false);
        });
    },
    [loadApplicationData, navigate, res, urlParams.applicationId],
  );

  const renderForm = useCallback(
    (formikProps: FormikProps<SubmitBlankMediaProfessionalUserApplication>) => {
      const editAllowed =
        !applicationData?.blankMediaTypeId && !submitted
          ? true
          : AvisUtils.isEditablePortalReportStatusTypeInfo(
              applicationData?.portalReportStatusType,
            ) && !submitted;

      if (isLoadingFirstTime) return <AvisLoader inline />;

      return (
        <>
          <AvisConfirmModalShort
            isOpen={isModalOpen}
            content={res("blankMedia_Application_notification_ConfirmSubmit")}
            confirm={formikProps.submitForm}
            cancel={() => setIsModalOpen(false)}
            title={res(
              "blankMedia_Application_notification_ConfirmSubmitTitle",
            )}
            disableControls={isSubmitting}
          />
          <BlankMediaTypeProfessionalUserApplicationForm
            applicationData={applicationData}
            bag={formikProps}
            periodInfo={periodInfo}
            equipments={equipments}
            importersAndManufacturers={importersAndManufacturers}
            sectionsVisible={isSectionsVisible(formikProps)}
            noDealAccepted={noDealAccepted}
            hasNoPortalReport={hasNoPortalReport}
            setNoDealAccepted={setNoDealAccepted}
            urlParams={urlParams}
            disabled={!editAllowed}
            loading={isLoadingImportersAndManufacturers || isLoadingPeriodInfo}
            onPeriodChange={loadRelatedPeriodData}
          />
          <ScrollToFieldError />
          {renderActionButtons(editAllowed, formikProps)}
        </>
      );
    },
    [
      applicationData,
      submitted,
      isLoadingFirstTime,
      isModalOpen,
      res,
      isSubmitting,
      periodInfo,
      equipments,
      importersAndManufacturers,
      isSectionsVisible,
      noDealAccepted,
      hasNoPortalReport,
      urlParams,
      isLoadingImportersAndManufacturers,
      isLoadingPeriodInfo,
      loadRelatedPeriodData,
      renderActionButtons,
    ],
  );

  return (
    <AvisForm
      pageTitle={pageTitleValue}
      error={error}
      loading={isLoadingApplicationData}
      backLink={{
        title: res("label_Back"),
        url: BlankMediaUtils.getUrlWithParams(
          BlankMediaUrls.applicationsList,
          {},
          true,
        ),
      }}
    >
      <Formik<SubmitBlankMediaImporterApplication>
        initialValues={getInitValues(applicationData)}
        onSubmit={handleSubmit}
        validationSchema={validationSchemaBlankMediaProfessionalUserApplication(
          res,
          noDealAccepted,
        )}
        validateOnBlur={false}
        validateOnChange={false}
        enableReinitialize
      >
        {renderForm}
      </Formik>
      {isSubmitting && <AvisLoader />}
    </AvisForm>
  );
}
